import {DOCUMENT} from '@angular/common';
import {AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild} from '@angular/core';

type QrScannerClass = typeof import('qr-scanner').default;
type QrScannerInstance = {
  start: () => Promise<void>;
  stop: () => void;
  destroy: () => void;
};

@Component({
  selector: 'sm-qr-scanner-overlay',
  templateUrl: './sm-qr-scanner-overlay.component.html',
  styleUrls: ['./sm-qr-scanner-overlay.component.scss'],
  standalone: false,
})
export class SmQrScannerOverlayComponent implements AfterViewInit, OnChanges, OnDestroy {
  private static workerConfigured = false;
  private scannerModulePromise: Promise<QrScannerClass> | null = null;
  private scanner: QrScannerInstance | null = null;
  private viewReady = false;
  private isDestroyed = false;
  private startScannerTimer: number | null = null;
  private overlayAttachTimer: number | null = null;
  private overlayAttachedToBody = false;

  @Input() externalScanner = false;
  @Input() externalLoading = false;
  @Input() externalError = '';
  @Input() retryLabel = '';
  @Output() retryRequested = new EventEmitter<void>();
  @Output() videoReady = new EventEmitter<HTMLVideoElement>();

  @Input() isOpen = false;
  @Input() title = '';
  @Input() hint = '';
  @Input() loadingLabel = '';
  @Input() unsupportedLabel = '';
  @Input() closeAriaLabel = '';

  @Output() closeRequested = new EventEmitter<void>();
  @Output() scanned = new EventEmitter<string>();

  @ViewChild('scannerOverlay', {read: ElementRef}) private readonly scannerOverlayRef?: ElementRef<HTMLDivElement>;
  @ViewChild('video', {read: ElementRef}) private readonly videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('overlay', {read: ElementRef}) private readonly overlayRef?: ElementRef<HTMLDivElement>;

  errorMessage = '';
  isStarting = false;
  isSupported: boolean | null = null;

  constructor(
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.isOpen) {
      this.queueOverlayAttach();
      this.queueScannerStart();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['isOpen']) {
      if (this.isOpen && this.viewReady) {
        this.queueOverlayAttach();
        this.queueScannerStart();
        return;
      }

      if (!this.isOpen) {
        this.clearQueuedScannerStart();
        this.clearQueuedOverlayAttach();
        await this.stopScanner();
        this.detachOverlayFromBody();
      }
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.clearQueuedOverlayAttach();
    this.detachOverlayFromBody();
    void this.stopScanner();
  }

  handleBackdropClick(): void {
    this.close();
  }

  handleCloseClick(): void {
    this.close();
  }

  private close(): void {
    this.clearQueuedScannerStart();
    this.clearQueuedOverlayAttach();
    void this.stopScanner();
    this.detachOverlayFromBody();
    this.ngZone.run(() => {
      this.closeRequested.emit();
    });
  }

  private queueOverlayAttach(): void {
    this.clearQueuedOverlayAttach();
    this.overlayAttachTimer = window.setTimeout(() => {
      this.overlayAttachTimer = null;
      if (this.isDestroyed || !this.isOpen || !this.viewReady) {
        return;
      }

      this.attachOverlayToBody();
    }, 0);
  }

  private queueScannerStart(): void {
    this.clearQueuedScannerStart();
    this.startScannerTimer = window.setTimeout(() => {
      this.startScannerTimer = null;
      if (this.isDestroyed || !this.isOpen || !this.viewReady) {
        return;
      }

      void this.startScanner();
    }, 0);
  }

  private clearQueuedScannerStart(): void {
    if (this.startScannerTimer === null) {
      return;
    }

    window.clearTimeout(this.startScannerTimer);
    this.startScannerTimer = null;
  }

  private clearQueuedOverlayAttach(): void {
    if (this.overlayAttachTimer === null) {
      return;
    }

    window.clearTimeout(this.overlayAttachTimer);
    this.overlayAttachTimer = null;
  }

  private attachOverlayToBody(): void {
    const scannerOverlay = this.scannerOverlayRef?.nativeElement;
    if (!scannerOverlay || this.overlayAttachedToBody || scannerOverlay.parentElement === this.document.body) {
      this.overlayAttachedToBody = Boolean(scannerOverlay);
      return;
    }

    this.renderer.appendChild(this.document.body, scannerOverlay);
    this.overlayAttachedToBody = true;
  }

  private detachOverlayFromBody(): void {
    const scannerOverlay = this.scannerOverlayRef?.nativeElement;
    if (!this.overlayAttachedToBody || !scannerOverlay || scannerOverlay.parentElement !== this.document.body) {
      this.overlayAttachedToBody = false;
      return;
    }

    this.renderer.removeChild(this.document.body, scannerOverlay);
    this.overlayAttachedToBody = false;
  }

  private async startScanner(): Promise<void> {
    if (!this.videoRef?.nativeElement || !this.overlayRef?.nativeElement || this.isStarting) {
      if (this.isOpen && !this.isDestroyed) {
        this.queueScannerStart();
      }
      return;
    }

    if (this.externalScanner) {
      this.videoReady.emit(this.videoRef.nativeElement);
      return;
    }

    this.errorMessage = '';
    this.isSupported = null;
    this.isStarting = true;

    try {
      await this.stopScanner();

      const QrScanner = await this.loadScannerModule();
      if (this.isDestroyed) {
        return;
      }

      const scanner = new QrScanner(
        this.videoRef.nativeElement,
        (result: {data: string}) => this.handleDetected(result.data),
        {
          preferredCamera: 'environment',
          highlightCodeOutline: true,
          highlightScanRegion: true,
          maxScansPerSecond: 8,
          overlay: this.overlayRef.nativeElement,
          returnDetailedScanResult: true,
          onDecodeError: () => {
            // Ignore frame misses while the scanner is active.
          },
        },
      );

      this.scanner = scanner;
      await scanner.start();
      this.isSupported = true;
    } catch (error) {
      if (this.isDestroyed) {
        return;
      }

      this.isSupported = false;
      this.errorMessage =
        error instanceof Error && error.message
          ? error.message
          : this.unsupportedLabel;
    } finally {
      this.isStarting = false;
    }
  }

  private async stopScanner(): Promise<void> {
    const scanner = this.scanner;
    this.scanner = null;

    if (!scanner) {
      return;
    }

    try {
      scanner.stop();
    } finally {
      scanner.destroy();
    }
  }

  private handleDetected(value: string): void {
    const token = value.trim();
    if (!token) {
      return;
    }

    this.ngZone.run(() => {
      this.scanned.emit(token);
      this.close();
    });
  }

  private async loadScannerModule(): Promise<QrScannerClass> {
    if (this.scannerModulePromise) {
      return this.scannerModulePromise;
    }

    this.scannerModulePromise = import('qr-scanner').then((module) => {
      if (!SmQrScannerOverlayComponent.workerConfigured) {
        module.default.WORKER_PATH = new URL('qr-scanner/qr-scanner-worker.min.js', import.meta.url).toString();
        SmQrScannerOverlayComponent.workerConfigured = true;
      }

      return module.default;
    });

    return this.scannerModulePromise;
  }
}
